import clsx from 'clsx';
import React, { useState, useEffect, useRef, RefObject, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router';
import { showToast } from '@/src/globalFunction';
import {
  useCreateProjectTag,
  useAttachInboxDocumentTag,
  useDetachInboxDocumentTag,
  useAttachLibraryFolderTag,
  useDetachLibraryFolderTag,
  useGetProjectTags,
  useDeleteProjectTag,
  useUpdateProjectTag,
  invalidateInboxQueries,
  invalidateLibraryQueries,
} from './api/projectTags';
import ConfirmTagActionModal from './Elements/ConfirmTagActionModal';
import TagEntryButton from './Elements/TagEntryButton';
import TagModal from './TagModal';
import { TagType } from './types';
import { freezeScrollContainer, useHasFullTagsAccess } from './utils';
import { useTagsStore } from '../Inbox2/store/tags.store';
import { useLibraryTagsStore } from '../Library/store/libraryTags.store';
import { useGetProjectPermissions } from '../api/getProjectPermissions';

export interface TagEntryProps {
  id: string;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  assignedTags?: TagType[];
  setAssignedTags?: (tags: TagType[]) => void;
  editTagIndex?: number | null;
  setEditTagIndex?: (idx: number | null) => void;
  isFilesTable?: boolean;
  allProjectTags?: TagType[];
  inboxDocumentId?: number | string;
  isLibrary?: boolean;
  onRequestUpdate?: (tagIndex: number, updatedTag: TagType) => void;
  isTagReadOnly?: boolean;
  onSave?: (tagIndex: number, updatedTag: TagType) => void;
  folderId?: number;
}

const TagEntry: React.FC<TagEntryProps> = ({
  id,
  openId,
  setOpenId,
  assignedTags = [],
  setAssignedTags,
  editTagIndex = null,
  setEditTagIndex,
  isFilesTable = false,
  isLibrary = false,
  inboxDocumentId,
  onRequestUpdate,
  isTagReadOnly = false,
  onSave,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tagPillRefs = useRef<RefObject<HTMLButtonElement>[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [localAssignedTags, setLocalAssignedTags] = useState<TagType[]>(assignedTags);
  const { projectId } = useParams<{ projectId: string }>();
  const createTagMutation = useCreateProjectTag(Number(projectId));
  const permissions = useTagsStore((state) => state.permissions);
  const hasFullTagsAccess = useHasFullTagsAccess(permissions?.library.actions);
  const libraryPermissionsQuery = useGetProjectPermissions({
    projectId: +projectId,
    config: { enabled: !!isLibrary, staleTime: Infinity },
  });
  const hasLibraryTagsAccess = useHasFullTagsAccess(
    libraryPermissionsQuery.data?.data.library?.actions
  );
  const contextualTagAccess = isLibrary ? hasLibraryTagsAccess : hasFullTagsAccess;
  const { data: projectTags, refetch: refetchProjectTags } = useGetProjectTags(projectId, {
    enabled: contextualTagAccess,
    keepPreviousData: true,
    staleTime: Infinity,
  });
  const updateProjectTagMutation = useUpdateProjectTag(projectId);
  const deleteProjectTagMutation = useDeleteProjectTag(projectId);
  const attachInboxTagMutation = useAttachInboxDocumentTag(Number(inboxDocumentId), {
    enable: !isLibrary && !isFilesTable,
  });
  const detachInboxTagMutation = useDetachInboxDocumentTag(Number(inboxDocumentId), {
    enable: !isLibrary && !isFilesTable,
  });
  const attachLibraryTagMutation = useAttachLibraryFolderTag(Number(inboxDocumentId), {
    enable: isLibrary,
  });
  const detachLibraryTagMutation = useDetachLibraryFolderTag(Number(inboxDocumentId), {
    enable: isLibrary,
  });
  const [selections, setSelections] = useState<TagType[]>(
    Array.isArray(projectTags) ? projectTags : []
  );
  const [suggestions] = useState<TagType[]>(Array.isArray(projectTags) ? projectTags : []);
  const [editTagId, setEditTagId] = useState<string | number | null>(null);
  const [editTag, setEditTag] = useState<TagType | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [newTagColor, setNewTagColor] = useState<string>('');
  const [modalStyle, setModalStyle] = useState<React.CSSProperties>({});
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [showConfirmUpdate, setShowConfirmUpdate] = useState<TagType | null>(null);
  const [showConfirmDeleteTag, setShowConfirmDeleteTag] = useState<TagType | null>(null);

  // Calculate and set modal position based on anchor element
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateModalPosition = (anchorRef: RefObject<HTMLButtonElement>) => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const modalDimensions = { width: 320, height: 300 };
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    };

    // Calculate initial position (centered horizontally below anchor)
    const position = {
      left:
        (isFilesTable ? 200 : 100) +
        (rect.left + viewport.scrollX + rect.width / 2 - modalDimensions.width / 2) -
        180,
      top: rect.bottom + viewport.scrollY - 50,
    };

    // Adjust vertical position if it would overflow viewport
    if (position.top + modalDimensions.height > viewport.height + viewport.scrollY) {
      position.top = rect.top + viewport.scrollY - modalDimensions.height + 100;
    }

    if (isTagReadOnly) {
      position.top = rect.top + viewport.scrollY - modalDimensions.height + 280;
    }

    // Ensure modal stays within horizontal bounds
    position.left = Math.max(
      8,
      Math.min(position.left, viewport.width + viewport.scrollX - modalDimensions.width - 8)
    );

    return {
      position: 'absolute' as const,
      left: position.left,
      top: position.top,
      zIndex: 99,
      maxWidth: modalDimensions.width,
      width: modalDimensions.width,
    };
  };

  // Position modal near the button or tag pill
  useEffect(() => {
    if (openId !== id) return;

    // Determine which element to anchor the modal to
    let anchorRef = buttonRef;
    if (editTagId != null) {
      const idx = assignedTags.findIndex((t) => t.id === editTagId);
      if (idx !== -1 && tagPillRefs.current[idx]) {
        anchorRef = tagPillRefs.current[idx];
      }
    }

    const newStyle = calculateModalPosition(anchorRef);
    if (newStyle) {
      setModalStyle(newStyle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, id, assignedTags, editTagId]);

  // Debug logging for openId and id changes
  useEffect(() => {
    if (openId === id) {
      setLocalAssignedTags(assignedTags);
    }
  }, [id, openId, assignedTags]);

  // Always sync localAssignedTags with assignedTags for robust UI updates
  useEffect(() => {
    setLocalAssignedTags(assignedTags);
  }, [assignedTags]);

  // Sync localAssignedTags from assignedTags only when modal is first opened
  useEffect(() => {
    if (openId === id && assignedTags.length > 0) {
      setLocalAssignedTags(assignedTags);
    }
    // Only run when modal transitions from closed to open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  // Handle edit mode separately
  useEffect(() => {
    if (openId === id && editTagIndex != null && assignedTags[editTagIndex]) {
      const tag = assignedTags[editTagIndex];
      setEditTag(tag);
      setEditTagId(tag.id);
      setTagInput(tag.name);
      setNewTagColor(tag.color || '');
    }
  }, [id, openId, editTagIndex, assignedTags]);

  // Keep selections and assigned tags in sync
  useEffect(() => {
    // Filter out any tags that are already assigned
    const filteredSelections = Array.isArray(projectTags)
      ? projectTags.filter(
          (tag) => !localAssignedTags?.some((assigned) => assigned.name === tag.name)
        )
      : [];
    setSelections(filteredSelections);
  }, [localAssignedTags, projectTags]);

  // Freeze scroll when modal is open to disable editing background content
  useEffect(() => {
    freezeScrollContainer(openId === id, ['.table-container', '.card--selectedEntryPanel']);
    return () => {
      freezeScrollContainer(false, ['.table-container', '.card--selectedEntryPanel']);
    };
  }, [openId, id]);

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpenId(id);
  };

  const handleClose = () => {
    // Only close modal if user explicitly requests it
    // Do NOT close modal after tag creation/assignment
    // Update parent state first
    if (setAssignedTags) {
      setAssignedTags(localAssignedTags);
    }

    // Reset all local state
    setEditTag(null);
    setEditTagId(null);
    setEditTagIndex?.(null);
    setTagInput('');
    setNewTagColor('');

    setOpenId(null);
  };

  // Updates local state immediately and parent state on close
  const updateAssignedTags = (newTags: TagType[]) => {
    setLocalAssignedTags(newTags);
  };

  // Removes a tag by label
  const handleRemoveTag = (name: string) => {
    setShowConfirmDelete(name); // Show confirm modal before actual deletion
    setModalStyle((prev) => ({ ...prev, opacity: 0 }));
  };

  const handleConfirmDelete = () => {
    if (!showConfirmDelete) return;
    const tagToRemove = localAssignedTags.find((tag) => tag.name === showConfirmDelete);
    const newTags = localAssignedTags.filter((tag) => tag.name !== showConfirmDelete);

    // Detach from inbox document if tag has a backend id
    if (tagToRemove && !isLibrary) {
      detachInboxTagMutation.mutate(tagToRemove.id as number);
    }
    if (tagToRemove && isLibrary) {
      detachLibraryTagMutation.mutate(tagToRemove.id as number);
    }

    // Delete tag from project
    if (tagToRemove && tagToRemove.id && tagToRemove.version) {
      deleteProjectTagMutation.mutate(
        { tagId: Number(tagToRemove.id), version: tagToRemove.version },
        {
          onSuccess: () => {
            updateAssignedTags(newTags);
            setShowConfirmDelete(null);
            setOpenId(null);
          },
        }
      );
    }
  };

  // Called when user submits EditTagForm (e.g. Save/Edit)
  const handleEditTagFormSubmit = (updatedTag: TagType) => {
    setShowConfirmUpdate(updatedTag); // Show confirm update modal
  };

  const handleConfirmUpdate = () => {
    if (!showConfirmUpdate) return;
    updateProjectTagMutation.mutate(
      {
        tagId: showConfirmUpdate.id,
        body: {
          name: showConfirmUpdate.name,
          color: showConfirmUpdate.color,
          version: showConfirmUpdate.version,
        },
      },
      {
        onSuccess: (updatedTag) => {
          const updatedTags = localAssignedTags.map((t) =>
            t.id === showConfirmUpdate.id ? { ...t, ...updatedTag } : t
          );
          setLocalAssignedTags(updatedTags);
          setAssignedTags && setAssignedTags(updatedTags);
          showToast('success', 'Tag updated successfully');
          if (!isLibrary) {
            invalidateInboxQueries();
          }
          setEditTag(null);
          setEditTagId(null);
          setEditTagIndex?.(null);
          setTagInput('');
          setNewTagColor('');
          setShowConfirmUpdate(null);
        },
        onError: () => {
          setShowConfirmUpdate(null);
        },
      }
    );
  };

  // Handler for deleting a tag from SelectionsGrid
  const handleDeleteTag = (tag: TagType) => {
    setShowConfirmDeleteTag(tag); // Open confirm modal instead of window.confirm
  };

  const handleConfirmDeleteTag = () => {
    if (!showConfirmDeleteTag) return;
    deleteProjectTagMutation.mutate(
      { tagId: Number(showConfirmDeleteTag.id), version: showConfirmDeleteTag.version },
      {
        onSuccess: () => {
          // Remove tag from local state/UI
          const updatedTags = localAssignedTags.filter((t) => t.id !== showConfirmDeleteTag.id);
          setLocalAssignedTags(updatedTags);
          setAssignedTags && setAssignedTags(updatedTags);
          setShowConfirmDeleteTag(null);
          showToast('success', 'Tag deleted successfully');
        },
      }
    );
  };

  // Initiates tag editing mode
  const handleEditTagLocal = (tag: TagType, idx: number) => {
    if (openId !== id) setOpenId(id);
    setEditTag(tag);
    setTagInput(tag.name);
    setEditTagId(tag.id);
    setEditTagIndex?.(idx);
  };

  // Restores a previously removed tag to selections
  const handleRestoreSuggestion = (name: string) => {
    // Find the tag in any of our collections
    const foundTag =
      assignedTags.find((tag) => tag.name === name) ||
      selections.find((tag) => tag.name === name) ||
      suggestions.find((tag) => tag.name === name);

    // Add to selections if not already present
    setSelections((prev) =>
      prev.some((s) => s.name === name) ? prev : foundTag ? [...prev, foundTag] : prev
    );
  };

  // Handles selection of an existing tag
  const handleSelections = (suggestion: string) => {
    // Find the tag in selections
    const selectedTag = selections.find((s) => s.name === suggestion);
    if (!selectedTag) return;

    // Only add if not already present, do not remove existing
    setLocalAssignedTags((prev) => {
      if (prev.some((t) => t.name === selectedTag.name)) return prev;
      return [...prev, selectedTag];
    });

    // Remove from selections
    setSelections((prev) => prev.filter((s) => s.name !== suggestion));

    // Attach the tag to the inbox document
    if (!isLibrary) {
      attachInboxTagMutation.mutate(Number(selectedTag.id));
    } else if (isLibrary) {
      attachLibraryTagMutation.mutate(Number(selectedTag.id));
    }

    setTagInput('');
  };

  const updateFileTags = useTagsStore((state) => state.setFileTags);
  const updateFolderTags = useLibraryTagsStore((state) => state.updateFolderTags);
  // Creates a new custom tag
  const handleCreateTag = (name: string, color: string) => {
    const trimmed = name.trim();
    if (
      trimmed &&
      !localAssignedTags?.some((t) => t.name === trimmed) &&
      !createTagMutation.isLoading
    ) {
      createTagMutation.mutate(
        { name: trimmed, color },
        {
          onSuccess: (newTag) => {
            const createdTag = newTag;
            const updatedTags = [...localAssignedTags, createdTag];
            setLocalAssignedTags(updatedTags);
            setAssignedTags && setAssignedTags(updatedTags);
            // Assign the created tag to the inbox document or library folder
            if (!isLibrary) {
              attachInboxTagMutation.mutate(Number(createdTag.id), {
                onSuccess: () => {
                  updateFileTags(String(inboxDocumentId), updatedTags);
                },
              });
            } else {
              attachLibraryTagMutation.mutate(Number(createdTag.id), {
                onSuccess: () => {
                  updateFolderTags(String(inboxDocumentId), updatedTags);
                  invalidateLibraryQueries();
                },
              });
            }
            setTagInput('');
          },
        }
      );
    }
  };

  // When user clicks save in edit mode:
  // Patch: Handle edit/save for newly created (local) tags
  const handleSave = () => {
    if (editTagIndex !== null) {
      const tagToEdit = localAssignedTags[editTagIndex];
      if (!tagToEdit) return;
      const updatedTag = {
        ...tagToEdit,
        name: tagInput,
        color: newTagColor || tagToEdit.color,
      };
      // If tag has no id or version, treat as local update
      if (!tagToEdit.id || !tagToEdit.version) {
        const updatedTags = localAssignedTags.map((tag, idx) =>
          idx === editTagIndex ? updatedTag : tag
        );
        setLocalAssignedTags(updatedTags);
        setAssignedTags?.(updatedTags);
        setEditTag(null);
        setEditTagId(null);
        setEditTagIndex?.(null);
        setTagInput('');
        setNewTagColor('');
      } else if (onRequestUpdate) {
        onSave?.(editTagIndex, updatedTag);
      }
    }
  };

  // Unassigns a tag (removes from file/document, not deletes from project)
  const handleUnassignTag = (name: string) => {
    const tagToUnassign = localAssignedTags.find((tag) => tag.name === name);
    if (!tagToUnassign) return;
    const newTags = localAssignedTags.filter((tag) => tag.name !== name);
    const syncLatestTags = () => {
      setLocalAssignedTags(newTags);
      setAssignedTags?.(newTags);
      refetchProjectTags();
    };
    if (!isLibrary) {
      detachInboxTagMutation.mutate(tagToUnassign.id as number);
    } else {
      detachLibraryTagMutation.mutate(tagToUnassign.id as number, {
        onSuccess: () => {
          syncLatestTags();
        },
      });
    }
  };

  // Utility to check if a tag name is duplicate
  const isDuplicateTag = (name: string) => {
    return (
      localAssignedTags?.some((t) => t.name === name) || assignedTags.some((t) => t.name === name)
    );
  };

  return (
    <div
      ref={containerRef}
      className={clsx('tw-relative', 'tw-w-full')}
      style={{ justifyItems: isFilesTable || isLibrary ? 'center' : 'start' }}
    >
      <TagEntryButton
        onClick={handleOpen}
        buttonRef={buttonRef}
        isHidden={openId === id}
        isTagReadOnly={isTagReadOnly}
        isFilesTable={isFilesTable}
        isLibrary={isLibrary}
      />
      {openId === id &&
        ReactDOM.createPortal(
          <TagModal
            open={true}
            modalStyle={modalStyle}
            buttonRef={(() => {
              const tagsArray = Array.isArray(localAssignedTags) ? localAssignedTags : [];
              const idx = tagsArray.findIndex((t) => t.id === editTagId);
              return editTagId != null && idx !== -1 ? tagPillRefs.current[idx] : buttonRef;
            })()}
            assignedTags={localAssignedTags}
            onRemoveTag={handleRemoveTag}
            onUnassignTag={handleUnassignTag}
            tagInput={tagInput}
            setTagInput={setTagInput}
            selections={selections}
            suggestions={selections}
            onSelectSuggestion={handleSelections}
            onCreateTag={handleCreateTag}
            onRestoreSuggestion={handleRestoreSuggestion}
            onEditTagFormSubmit={handleEditTagFormSubmit}
            onDeleteTag={handleDeleteTag}
            editTag={editTag}
            newTagColor={newTagColor}
            setNewTagColor={setNewTagColor}
            onUpdateTag={(oldLabel, newLabel, color) => {
              const updatedTags = localAssignedTags.map((t) =>
                t.name === oldLabel ? { ...t, name: newLabel, color } : t
              );
              setLocalAssignedTags(updatedTags);
              setAssignedTags?.(updatedTags);
              setEditTag(null);
              setEditTagId(null);
              setEditTagIndex?.(null);
              setTagInput('');
              setNewTagColor('');
            }}
            onClose={() => {
              if (openId === id) {
                handleClose();
                setEditTag(null);
                setEditTagId(null);
                setEditTagIndex?.(null);
                setNewTagColor('');
              }
            }}
            onSave={handleSave}
            isTagReadOnly={isTagReadOnly}
            onEditTag={(tag: TagType) =>
              handleEditTagLocal(
                tag,
                (Array.isArray(localAssignedTags) ? localAssignedTags : []).findIndex(
                  (t) => t.id === tag.id
                )
              )
            }
            // Disable create button if duplicate
            disableCreateButton={isDuplicateTag(tagInput)}
          />,
          document.body
        )}
      {showConfirmDelete && (
        <ConfirmTagActionModal
          onClose={() => setShowConfirmDelete(null)}
          onSubmit={handleConfirmDelete}
          subject={'Tag'}
          actionType="delete"
        />
      )}
      {showConfirmUpdate && (
        <ConfirmTagActionModal
          onClose={() => setShowConfirmUpdate(null)}
          onSubmit={handleConfirmUpdate}
          subject={'Tag'}
          actionType="update"
        />
      )}
      {showConfirmDeleteTag && (
        <ConfirmTagActionModal
          onClose={() => setShowConfirmDeleteTag(null)}
          onSubmit={handleConfirmDeleteTag}
          subject={'Tag'}
          actionType="delete"
        />
      )}
    </div>
  );
};

export default TagEntry;
